/**
 * Where a render error lands (2026-09-08), in two shapes of the same promise
 * that a bug shows a sentence instead of a blank page:
 *  - `RouteError` is the router's `errorElement` for the root and the shell
 *    (routes.tsx): what broke, a reload, and a way back to the products.
 *  - `ErrorBoundary` is a class (React has no hook for this) around one
 *    region whose failure must not take its neighbours down; the caller draws
 *    the fallback, and keys the boundary to start it over when what it wraps
 *    changes (the diagram pane keys on the drawing).
 * Both hand the error to lib/errorReporting: React catches a render error
 * before window.onerror ever hears of it, so this is the only place a
 * broken page can be reported from.
 */

import { Component, useEffect, type ErrorInfo, type ReactNode } from 'react';
import { isRouteErrorResponse, Link, useRouteError } from 'react-router';

import { reportError } from './errorReporting';

/** One line a reader can quote: the router's status line, the error's message, or a plain default. */
export function errorMessageOf(error: unknown): string {
  if (isRouteErrorResponse(error)) return `${error.status} ${error.statusText}`.trim();
  if (error instanceof Error && error.message.length > 0) return error.message;
  return 'Something went wrong.';
}

export function RouteError() {
  const error = useRouteError();

  useEffect(() => {
    // The router's own responses (a 404 for a path nobody routes) are not bugs.
    if (!isRouteErrorResponse(error)) reportError(error, { where: 'route' });
  }, [error]);

  return (
    <div
      data-testid="route-error"
      role="alert"
      className="flex h-full min-h-0 flex-col items-start justify-center gap-1 bg-surface px-8"
    >
      <h1 className="mt-0 mb-2 text-[length:var(--text-title-size)] leading-[var(--text-title-line)] font-semibold">
        Something broke
      </h1>
      <p className="mt-0 text-secondary">{errorMessageOf(error)}</p>
      <p className="text-secondary">
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="cursor-pointer border-none bg-transparent p-0 text-[length:inherit] text-(--accent) underline underline-offset-2"
        >
          Reload the page
        </button>
        {' or '}
        <Link to="/products" className="text-(--accent) underline underline-offset-2">
          go back to your products
        </Link>
        .
      </p>
    </div>
  );
}

export class ErrorBoundary extends Component<
  { children: ReactNode; fallback: (error: unknown) => ReactNode },
  { failed: boolean; error: unknown }
> {
  state: { failed: boolean; error: unknown } = { failed: false, error: null };

  static getDerivedStateFromError(error: unknown): { failed: boolean; error: unknown } {
    return { failed: true, error };
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    reportError(error, { componentStack: info.componentStack });
  }

  render(): ReactNode {
    return this.state.failed ? this.props.fallback(this.state.error) : this.props.children;
  }
}
